import { useEffect, useRef } from 'react';
import { Crown, Lock } from 'lucide-react';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import BottomNav from './BottomNav';
import { perfLog, perfNow } from '../utils/perf';

/**
 * Gates the entire meal-planning experience behind Fitxess Pro. Free users see
 * an upgrade prompt (which opens the paywall) instead of any planning screen.
 * Pro, active-trial, and admin-granted Free Pro users (tier !== free) pass
 * straight through. The backend also enforces this on every generation route —
 * this is the UX layer, not the security boundary.
 *
 * `loading` is true ONLY until the user's entitlement is first resolved; a
 * later background subscription refresh never brings this spinner back, so
 * the planning screen is never unmounted by one. The spinner occupies only the
 * content area — BottomNav stays so the user can always leave Plan. (The
 * children render their own BottomNav, so there is never a second one.)
 */
export default function PlanningGate({ children }) {
  const { isFree, loading, showPaywall } = useSubscriptionContext();

  // [PERF] which gate state Plan is in, and when (temporary instrumentation).
  const mountRef = useRef(null);
  if (mountRef.current === null) mountRef.current = perfNow();
  const gateState = loading ? 'UNKNOWN' : (isFree ? 'FREE' : 'PRO');
  useEffect(() => {
    perfLog('[PLAN_GATE][' + gateState + ']', { since_gate_mount_ms: perfNow() - mountRef.current });
  }, [gateState]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-bottom-nav" data-testid="planning-gate-loading">
        <div className="min-h-[60vh] grid place-items-center">
          <span className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
        </div>
        <BottomNav />
      </div>
    );
  }

  if (!isFree) return children;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center text-center px-8 pb-28"
      data-testid="planning-pro-gate"
    >
      <span className="size-16 rounded-full bg-accent/15 grid place-items-center">
        <Lock className="size-7 text-accent" />
      </span>
      <h1 className="mt-5 text-2xl font-heading font-bold">Meal planning is a Fitxess Pro feature</h1>
      <p className="mt-2 text-sm text-muted-foreground max-w-sm">
        Build personalized daily and weekly meal plans with optional leftovers,
        auto grocery lists, and unlimited replanning.
      </p>
      <button
        type="button"
        onClick={() => showPaywall('can_generate_weekly_plan')}
        className="mt-6 h-12 px-6 rounded-xl bg-gradient-to-r from-accent to-cyan-500 text-white text-sm font-semibold flex items-center gap-2"
        data-testid="planning-gate-upgrade"
      >
        <Crown className="size-4" /> Upgrade to Fitxess Pro
      </button>
      <BottomNav />
    </div>
  );
}
